// COUPLING: everything here patches the SVG that Apex draws, through its
// internal class names (`g.apexcharts-bar-series`, `path.apexcharts-bar-area`,
// `.apexcharts-xaxis-label`, `.apexcharts-yaxis-annotations`,
// `.apexcharts-inner`) and the `el` on its chart instance. None of that is
// public API. Verified against apexcharts 7.8.0 (node_modules); re-check these
// selectors when upgrading it.
//
// Bars have 16px top and 4px bottom corners, lifted 8px off the x-axis line —
// the expanded card's design. A narrow bar gets a proportionally smaller top
// radius, else 16px reads as a half circle.
export const TOP_RADIUS_PX = 16
const TOP_RADIUS_SHARE = 0.25
const BOTTOM_RADIUS_PX = 4
const AXIS_GAP_PX = 8

/** The part of Apex's chart instance used here (its types don't cover `el`). */
export interface ApexChart {
  el: HTMLElement
  w?: {
    globals?: {
      maxYArr?: number[]
      minYArr?: number[]
      gridHeight?: number
    }
  }
}

const isApexChart = (chart: unknown): chart is ApexChart =>
  typeof chart === 'object' &&
  chart !== null &&
  'el' in chart &&
  chart.el instanceof HTMLElement

const chartElement = (chart: unknown): HTMLElement | undefined =>
  isApexChart(chart) ? chart.el : undefined

/** The data point an Apex event's options name, if they name one. */
export const dataPointIndexOf = (options: unknown): number | null =>
  typeof options === 'object' &&
  options !== null &&
  'dataPointIndex' in options &&
  typeof options.dataPointIndex === 'number'
    ? options.dataPointIndex
    : null

/** The data point a click names; its options carry it as a string. */
export const clickedIndexOf = (options: unknown): number | null => {
  if (typeof options !== 'object' || options === null) return null
  if (!('dataPointIndex' in options)) return null
  const raw = options.dataPointIndex
  const index = typeof raw === 'string' && raw !== '' ? Number(raw) : raw
  return typeof index === 'number' && Number.isInteger(index) && index >= 0
    ? index
    : null
}

// Apex leaves line markers hidden without an animation: show them, and hide the ~0px stray ones.
export const showMarkers = (chart: unknown) => {
  const root = chartElement(chart)
  root
    ?.querySelectorAll('.apexcharts-series-markers-wrap')
    .forEach(wrap => wrap.classList.remove('apexcharts-element-hidden'))
  root
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-marker')
    .forEach(marker => {
      if (marker.getBBox().width < 1) marker.style.display = 'none'
    })
}

// Apex has no gap option between the bars and the axis: move the series group.
export const liftBarsOffAxis = (chart: unknown) => {
  const series = chartElement(chart)?.querySelector<SVGGElement>(
    'g.apexcharts-bar-series'
  )
  if (series) series.style.transform = `translateY(-${AXIS_GAP_PX}px)`
}

// Pixels per data unit on the value axis, from the chart's own rendered scale.
const pixelsPerUnit = (chart: unknown): number | null => {
  const globals = isApexChart(chart) ? chart.w?.globals : undefined
  const span = (globals?.maxYArr?.[0] ?? 0) - (globals?.minYArr?.[0] ?? 0)
  const height = globals?.gridHeight ?? 0
  return span > 0 && height > 0 ? height / span : null
}

// Apex draws a column down to 0: on an axis starting above it, keep the part above the minimum.
const clipBar = (rawH: number, yMin: number, value: number | undefined) =>
  yMin > 0 && value !== undefined && value > 0
    ? (rawH * (value - yMin)) / value
    : rawH

/** A bar's outline: round on top, a small radius below. */
const roundedBarPath = (
  x: number,
  y: number,
  w: number,
  h: number,
  top: number,
  bottom: number
) =>
  `M${x + top} ${y}H${x + w - top}A${top} ${top} 0 0 1 ${x + w} ${y + top}` +
  `V${y + h - bottom}A${bottom} ${bottom} 0 0 1 ${x + w - bottom} ${y + h}` +
  `H${x + bottom}A${bottom} ${bottom} 0 0 1 ${x} ${y + h - bottom}` +
  `V${y + top}A${top} ${top} 0 0 1 ${x + top} ${y}Z`

const barRadii = (w: number, h: number) => ({
  top: Math.min(TOP_RADIUS_PX, w * TOP_RADIUS_SHARE, h / 2),
  bottom: Math.min(BOTTOM_RADIUS_PX, w / 2, h / 2),
})

// Gives each bar the design's round top / small bottom shape, and a clip of it for strokes.
let clipSequence = 0
export const shapeBars = (
  chart: unknown,
  yMin = 0,
  values: (number | undefined)[] = []
) => {
  const svg = chartElement(chart)?.querySelector('svg')
  if (!svg) return
  const ns = 'http://www.w3.org/2000/svg'
  const defs =
    svg.querySelector('defs') ??
    svg.insertBefore(document.createElementNS(ns, 'defs'), svg.firstChild)
  svg
    .querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach((bar, i) => {
      const { x, y, width: w, height: rawH } = bar.getBBox()
      // Skip a bar Apex has not repainted since (its height is our last), unless its clip is gone.
      const id = bar.dataset['clipId']
      if (
        id !== undefined &&
        Math.abs(rawH - Number(bar.dataset['lastH'] ?? NaN)) < 0.05 &&
        defs.querySelector(`#${id}`)
      )
        return
      const h = clipBar(rawH, yMin, values[i])
      if (!(w > 0 && h > 0)) return
      const { top, bottom } = barRadii(w, h)
      const d = roundedBarPath(x, y, w, h, top, bottom)
      bar.setAttribute('d', d)
      bar.dataset['lastH'] = String(h)
      const clipId = id ?? `ln-bar-clip-${clipSequence++}`
      bar.dataset['clipId'] = clipId
      let clip = defs.querySelector(`#${clipId}`)
      if (!clip) {
        clip = document.createElementNS(ns, 'clipPath')
        clip.id = clipId
        clip.appendChild(document.createElementNS(ns, 'path'))
        defs.appendChild(clip)
      }
      clip.firstElementChild?.setAttribute('d', d)
      bar.setAttribute('clip-path', `url(#${clipId})`)
    })
}

const RING_PX = 4
const RING_CLASS = 'ln-bar-ring'
const GHOST_CLASS = 'ln-ghost'

const removeAll = (chart: unknown, className: string) =>
  chartElement(chart)
    ?.querySelectorAll(`.${className}`)
    .forEach(node => node.remove())

const insertPathBefore = (
  bar: SVGPathElement,
  className: string,
  d: string
) => {
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('class', className)
  path.setAttribute('d', d)
  bar.parentNode?.insertBefore(path, bar)
}

export const hideBarRing = (chart: unknown) => removeAll(chart, RING_CLASS)

// Hides the tooltip, crosshairs and bar ring, for a pointer that left with the browser.
export const hideHover = (root: HTMLElement | null) => {
  if (!root) return
  root
    .querySelectorAll('.apexcharts-tooltip, .apexcharts-xcrosshairs')
    .forEach(node => node.classList.remove('apexcharts-active'))
  root.querySelectorAll(`.${RING_CLASS}`).forEach(node => node.remove())
}

// The hover ring: a larger shape behind the bar (which is clipped); the selected bar has none.
export const showBarRing = (chart: unknown, index: number) => {
  hideBarRing(chart)
  const bar = chartElement(chart)?.querySelectorAll<SVGPathElement>(
    'path.apexcharts-bar-area'
  )[index]
  if (!bar?.parentNode || bar.dataset['kind'] === 'selected') return
  const { x, y, width: w, height: h } = bar.getBBox()
  if (!(w > 0 && h > 0)) return
  const { top, bottom } = barRadii(w, h)
  insertPathBefore(
    bar,
    RING_CLASS,
    roundedBarPath(
      x - RING_PX,
      y - RING_PX,
      w + 2 * RING_PX,
      h + 2 * RING_PX,
      top + RING_PX,
      bottom + RING_PX
    )
  )
}

// The outlook of a year under way: a pale bar behind the year's own, up to the projection.
export const drawGhosts = (
  chart: unknown,
  yMin: number,
  ghosts: (number | null)[]
) => {
  removeAll(chart, GHOST_CLASS)
  const perUnit = pixelsPerUnit(chart)
  if (!perUnit) return
  chartElement(chart)
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach(bar => {
      const ghost = ghosts[Number(bar.getAttribute('j'))]
      if (ghost === null || ghost === undefined) return
      const { x, y, width: w, height: h } = bar.getBBox()
      const height = (ghost - yMin) * perUnit
      if (!(w > 0 && h > 0 && height > h)) return
      const { top, bottom } = barRadii(w, height)
      insertPathBefore(
        bar,
        GHOST_CLASS,
        roundedBarPath(x, y + h - height, w, height, top, bottom)
      )
    })
}

// Marks bars (selected, projected) for the CSS by their data point, Apex's `j`; run after each draw.
export const markBars = (chart: unknown, kinds: (string | undefined)[]) =>
  chartElement(chart)
    ?.querySelectorAll<SVGPathElement>('path.apexcharts-bar-area')
    .forEach(bar => {
      const kind = kinds[Number(bar.getAttribute('j'))]
      if (kind) bar.dataset['kind'] = kind
      else delete bar.dataset['kind']
    })

// Emphasises the hovered and selected x labels; matched by text, as Apex may drop labels.
export const emphasiseLabels = (
  chart: unknown,
  ...emphasised: (string | null | undefined)[]
) =>
  chartElement(chart)
    ?.querySelectorAll<SVGTextElement>('.apexcharts-xaxis-label')
    .forEach(text => {
      const label = text.textContent?.trim() ?? ''
      const on = emphasised.some(
        entry =>
          entry !== null && entry !== undefined && label.startsWith(entry)
      )
      text.style.fontWeight = on ? '500' : ''
      text.style.fill = on ? 'var(--ln-text)' : ''
    })
